import { useCallback, useState } from "react";
import { useObrasCommonReports } from "../components/hooks/obras_common_reports";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { Obra } from "../../domain/models/obra";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useObrasTopHeaderState } from "../components/hooks/obras_top_header_state";
import { useLocation } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { GetAllObrasServicosControladosModel } from "../../domain/models/obras_servicos_controlados";
import { GetObraServicosControladosDropDown } from "../../data/daos/read/get_obra_servicos_controlados_dropdown";
import { ObrasServicosControladosForm } from "../schemas/Obras_ObrasServicosControlados/Obras_ObrasServicosControlados";
import { useObrasServicosControladosPagination } from "../hooks/Obras_ObrasServicosControlados/pagination";
import { TelasIds } from "../../../../common/utils/tela_ids";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";

type LoadObraFuncState = {
  idObra?: number;
};

type LoadServicosFuncState = {
  IdObra?: number | null;
  CurrentPage?: number | null;
  RowsPerPage?: number | null;
};

export const useObrasServicosControladosViewModel = () => {
  const {
    obras: {
      getAllObrasServicosControladosUseCase,
      deleteObraServicosControladosUseCase,
      obraServicosControladosDropDownUseCase,
      createObraServicosControladosUseCase,
      getObraByIdUseCase,
    },
  } = useDependencyInjection();
  const backOrNavigate = useBackOrNavigate();
  const [idObra, setIdObra] = useState<number>();
  const [obrasReportState, initializeReportState] = useObrasCommonReports({ idObra });
  const [errorMessage, setErrorMessage] = useState<string>();
  const [selectedObra, setSelectedObra] = useState<Obra>();
  const obrasTopHeaderState = useObrasTopHeaderState();
  const location = useLocation();
  const pagination = useObrasServicosControladosPagination();
  const [loading, setLoading] = useState(false);
  const { pushErrorNotification } = usePageLoader();
  const [obrasServicosControlados, setObrasServicos] = useState<GetAllObrasServicosControladosModel[]>([]);
  const [obrasServicosDropDown, setObrasServicosDropDown] = useState<GetObraServicosControladosDropDown[]>([]);
  const [selectedServicoId, setSelectedServicoId] = useState<number>();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [saving, setSaving] = useState(false);

  usePageStartBootstrap(async () => {
    await handleReload();
  });

  const loadObra = useCallback(
    async (funcState?: LoadObraFuncState) => {
      try {
        const filteredIdObra = funcState?.idObra ?? idObra;

        const obra = await getObraByIdUseCase.getObraById(TelasIds.Obras_servicoscontrolados, filteredIdObra);

        setSelectedObra(obra);
      } catch (e: unknown) {
        setSelectedObra(undefined);
        throw e;
      }
    },
    [idObra, getObraByIdUseCase],
  );

  const handleBack = useCallback(() => {
    backOrNavigate("/Obras_lista");
  }, [backOrNavigate]);

  //#region GET all
  const load = useCallback(
    async (funcState?: LoadServicosFuncState) => {
      try {
        const filterPage = funcState?.CurrentPage ?? pagination.page;
        const filterRows = funcState?.RowsPerPage ?? pagination.rowsPerPage;
        const filterIdObra = funcState?.IdObra ?? idObra;

        await getAllObrasServicosControladosUseCase.getAllObrasServicosControlados(filterIdObra, filterPage, filterRows);

        const resposta = await obraServicosControladosDropDownUseCase.obraServicosControladosDropDown(filterIdObra);
        setObrasServicosDropDown(resposta);
        setObrasServicos(getAllObrasServicosControladosUseCase.items);
        pagination.setRowsPerPage(getAllObrasServicosControladosUseCase.pageSize);
        pagination.setTotalItems(getAllObrasServicosControladosUseCase.totalItems);
      } catch (e: unknown) {
        throw e;
      }
    },
    [getAllObrasServicosControladosUseCase, pagination, idObra, obraServicosControladosDropDownUseCase],
  );

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage(undefined);
      const id = Number(new URLSearchParams(location.search).get("id"));

      if (!Number.isInteger(id) || id <= 0) {
        setErrorMessage("Falha ao recuperar id da obra!");
        return;
      }

      setIdObra(id);
      await loadObra({ idObra: id });
      await load({ IdObra: id });
      initializeReportState(id);
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados da página!")[0]);
    } finally {
      setLoading(false);
    }
  }, [location.search, loadObra, load, initializeReportState]);

  const handlePageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        useObrasServicosControladosPagination.setState({ page: page + 1 });
        await load({ CurrentPage: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da página!"));
      } finally {
        setLoading(false);
      }
    },
    [pushErrorNotification, load],
  );

  const handleRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        useObrasServicosControladosPagination.setState({ page: 1, rowsPerPage });
        await load({ CurrentPage: 1, RowsPerPage: rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da página!"));
      } finally {
        setLoading(false);
      }
    },
    [pushErrorNotification, load],
  );
  //endregion

  //#region Delete
  const handleDeleteObraServicoOpenStateModal = useCallback((idServico: number | null, openState: boolean) => {
    if (openState) {
      setSelectedServicoId(idServico ?? undefined);
    }
    setOpenDeleteModal(openState);
  }, []);

  const handleDeleteObraServicoConfirm = useCallback(async () => {
    if (selectedServicoId === undefined) return;
    try {
      setSaving(true);
      await deleteObraServicosControladosUseCase.deleteObraServicosControlados(selectedServicoId);
      const page = obrasServicosControlados.length === 1 ? Math.max(1, pagination.page - 1) : pagination.page;
      useObrasServicosControladosPagination.setState({ page });
      await load({ CurrentPage: page });
      setOpenDeleteModal(false);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao excluir serviço!"));
    } finally {
      setSaving(false);
    }
  }, [selectedServicoId, deleteObraServicosControladosUseCase, obrasServicosControlados.length, pagination.page, load, pushErrorNotification]);

  //#endregion

  const handleObraServicosIncluirSubmit = useCallback(
    async (form: ObrasServicosControladosForm) => {
      try {
        setSaving(true);
        await createObraServicosControladosUseCase.createObraServicosControlados({
          idServicosControlados: form.idServicosControlados,
          idObra: idObra,
        });
        await load();
        setCreateModal(false);
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao salvar serviço!"));
      } finally {
        setSaving(false);
      }
    },
    [createObraServicosControladosUseCase, pushErrorNotification, idObra, load],
  );

  const handleOpenObrasServicosControladosModal = useCallback((openState: boolean) => {
    setCreateModal(openState);
  }, []);

  return {
    obrasReportState,
    selectedObra,
    loading,
    obrasServicosControlados,
    obrasTopHeaderState,
    pagination,
    openDeleteModal,
    obrasServicosDropDown,
    createModal,
    saving,
    errorMessage,
    handleReload,
    handleBack,
    handlePageChange,
    handleRowsPerPageChange,
    handleDeleteObraServicoConfirm,
    handleDeleteObraServicoOpenStateModal,
    handleOpenObrasServicosControladosModal,
    handleObraServicosIncluirSubmit,
  };
};
